import { yupResolver } from '@hookform/resolvers/yup';
import { Breadcrumb, Button } from 'antd';
import { FormProvider, useForm } from 'react-hook-form';
import { Link, useNavigate } from 'react-router';

import {
  useCreateAboutSchoolMutation,
  useUpdateAboutSchoolMutation,
  type IAboutSchool,
} from '@/api/endpoints/mainPages';
import { DataSection, InputField } from '@/shared/components';
import { ROUTES } from '@/shared/consts';
import { useApiRequest } from '@/shared/hooks';

import { ABOUT_SCHOOL_VALIDATION_SCHEMA } from './consts';
import styles from './MainAboutSchoolPage.module.scss';
import type { IFormData } from './types';

interface IProp {
  aboutSchoolData: IAboutSchool | null;
  pageName: string;
  pageId: number;
}

export const MainAboutSchoolPageComponent = ({ aboutSchoolData, pageId, pageName }: IProp) => {
  const navigate = useNavigate();

  const { main_text: mainText, motivational_text: motivationalText } = aboutSchoolData ?? {};

  const sendApiRequest = useApiRequest();

  const [createAboutSchool, { isLoading: isAboutSchoolCreating }] = useCreateAboutSchoolMutation();
  const [updateAboutSchool, { isLoading: isAboutSchoolUpdating }] = useUpdateAboutSchoolMutation();

  const methods = useForm<IFormData>({
    defaultValues: {
      mainText: mainText ?? '',
      motivationalText: motivationalText ?? '',
    },
    mode: 'onChange',
    resolver: yupResolver(ABOUT_SCHOOL_VALIDATION_SCHEMA),
  });

  const { handleSubmit } = methods;

  const saveHandler = handleSubmit(({ mainText, motivationalText }) => {
    sendApiRequest({
      successMessage: 'Данные успешно сохранены',
      apiRequestCallback: () => {
        const apiRequestCallback = aboutSchoolData ? updateAboutSchool : createAboutSchool;

        return apiRequestCallback({
          id: pageId,
          data: {
            main_text: mainText,
            motivational_text: motivationalText,
          },
        });
      },
    });
  });

  return (
    <div className={styles.wrapper}>
      <Breadcrumb
        items={[
          { title: 'Публичные страницы' },
          { title: <Link to={`/${ROUTES.PUBLIC_PAGES}/${ROUTES.MAIN}`}>Главная</Link> },
          { title: <Link to={`/${ROUTES.PUBLIC_PAGES}/${ROUTES.MAIN}/${pageId}`}>{pageName}</Link> },
          { title: 'О школе' },
        ]}
      />
      <FormProvider {...methods}>
        <DataSection
          titleText="Основной текст блока"
          isTitleRequired
        >
          <InputField
            fieldName="mainText"
            placeholder="Основной текст"
            shouldEnableLengthCounter
            maxLengthCount={500}
          />
        </DataSection>
        <DataSection
          titleText="Мотивационный текст блока"
          isTitleRequired
        >
          <InputField
            fieldName="motivationalText"
            placeholder="Мотивационный текст"
            shouldEnableLengthCounter
            maxLengthCount={500}
          />
        </DataSection>
        <div className={styles['buttons-wrapper']}>
          <Button
            className={styles.button}
            size="large"
            type="dashed"
            onClick={() => navigate(`/${ROUTES.PUBLIC_PAGES}/${ROUTES.MAIN}/${pageId}`)}
          >
            Назад
          </Button>
          <Button
            className={styles.button}
            size="large"
            type="primary"
            onClick={saveHandler}
            disabled={isAboutSchoolCreating || isAboutSchoolUpdating}
          >
            Сохранить
          </Button>
        </div>
      </FormProvider>
    </div>
  );
};
